import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Hourglass, Bell, Loader2, ChevronDown, ChevronUp, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { contentCalendarAPI } from '../lib/api';

const fmtShort = (ymd) => (ymd ? new Date(`${ymd.slice(0, 10)}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : '—');
const proxTxt = (d) => (d === null || d === undefined ? '' : d < 0 ? `hace ${-d} d (atrasada)` : d === 0 ? 'hoy' : d === 1 ? 'mañana' : `en ${d} días`);
const isUrgentPost = (p) => p.dias_esperando >= 5 || (p.dias_para_publicar !== null && p.dias_para_publicar <= 2);

function ClientRow({ g, onRemind, sending, navigate }) {
  const [open, setOpen] = useState(false);
  const red = g.urgente;
  return (
    <div className={`rounded-lg border ${red ? 'border-red-300 bg-red-50/60' : 'bg-background'}`} data-testid={`pending-client-${g.client_id}`}>
      <div className="flex items-center gap-3 p-3 flex-wrap">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 min-w-0 flex-1 text-left" data-testid={`pending-client-toggle-${g.client_id}`}>
          {red && <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />}
          <span className="font-semibold text-sm truncate">{g.cliente}</span>
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>
        <div className="flex items-center gap-4 text-xs">
          <span data-testid={`pending-client-count-${g.client_id}`}><b className="text-sm">{g.count}</b> pendiente{g.count === 1 ? '' : 's'}</span>
          <span className={g.max_dias_esperando >= 5 ? 'text-red-700 font-semibold' : 'text-muted-foreground'} data-testid={`pending-client-oldest-${g.client_id}`}>
            la más antigua: {g.max_dias_esperando} {g.max_dias_esperando === 1 ? 'día' : 'días'}
          </span>
          <span className={g.proxima_dias !== null && g.proxima_dias <= 2 ? 'text-red-700 font-semibold' : 'text-muted-foreground'} data-testid={`pending-client-next-${g.client_id}`}>
            {g.proxima_dias !== null && g.proxima_dias < 0 ? 'atrasada' : 'próxima'}: {fmtShort(g.proxima_fecha)} {g.proxima_dias !== null && `(${proxTxt(g.proxima_dias)})`}
          </span>
        </div>
        <Button size="sm" variant={red ? 'default' : 'outline'} className={red ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
          disabled={sending === g.client_id || (!g.has_discord && !g.has_portal)} onClick={() => onRemind(g)}
          title={!g.has_discord && !g.has_portal ? 'El cliente no tiene Discord ni acceso al portal' : 'Discord del cliente + notificación en su portal'}
          data-testid={`pending-client-remind-${g.client_id}`}>
          {sending === g.client_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Bell className="w-3.5 h-3.5 mr-1" />} Enviar recordatorio
        </Button>
      </div>
      {g.ultimo_recordatorio && <p className="px-3 -mt-2 pb-2 text-[11px] text-muted-foreground">Último recordatorio: {new Date(g.ultimo_recordatorio).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>}
      {open && (
        <div className="border-t divide-y" data-testid={`pending-client-posts-${g.client_id}`}>
          {g.posts.map((p) => (
            <button key={p.id} onClick={() => navigate(`/service-delivery?client=${g.client_id}&post=${p.id}`)}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left text-xs hover:bg-muted/50 ${isUrgentPost(p) ? 'text-red-700' : ''}`} data-testid={`pending-post-${p.id}`}>
              <span className="font-medium flex-1 truncate">{p.titulo}</span>
              <span title={p.visto ? 'El cliente ya la ha abierto' : 'El cliente aún no la ha abierto'}>{p.visto ? <Eye className="w-3.5 h-3.5 text-emerald-600" /> : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}</span>
              <span className="w-28 text-right">{fmtShort(p.fecha)} · {proxTxt(p.dias_para_publicar)}</span>
              <span className={`w-20 text-right ${p.dias_esperando >= 5 ? 'font-semibold' : ''}`}>{p.dias_esperando} {p.dias_esperando === 1 ? 'día' : 'días'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PendingApprovalsPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(null);
  const load = useCallback(() => contentCalendarAPI.approvalsOverview().then(setData).catch(() => setData({ clients: [], total: 0 })), []);
  useEffect(() => { load(); }, [load]);

  const remind = async (g) => {
    setSending(g.client_id);
    try {
      const r = await contentCalendarAPI.sendApprovalReminder(g.client_id);
      if (r?.sent === false) toast.error(r.message || 'No se pudo enviar');
      else toast.success(`Recordatorio enviado a ${g.cliente}`);
      load();
    } catch (e) { toast.error(e?.message || 'No se pudo enviar el recordatorio'); }
    finally { setSending(null); }
  };

  if (!data) return null;
  const urgentes = data.clients.filter((g) => g.urgente).length;
  return (
    <Card data-testid="pending-approvals-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Hourglass className="w-4 h-4 text-amber-600" /> Pendientes de aprobación del cliente
          <span className="text-xs font-normal text-muted-foreground" data-testid="pending-approvals-total">· {data.total} publicaciones en {data.clients.length} {data.clients.length === 1 ? 'cliente' : 'clientes'}</span>
          {urgentes > 0 && <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-red-100 text-red-700" data-testid="pending-approvals-urgent">{urgentes} urgente{urgentes === 1 ? '' : 's'}</span>}
        </CardTitle>
        <p className="text-xs text-muted-foreground">En rojo: se publica en 2 días o menos, o lleva 5 días o más esperando.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.clients.length === 0
          ? <p className="text-sm text-muted-foreground text-center py-4" data-testid="pending-approvals-empty">Ningún cliente tiene publicaciones pendientes de aprobar.</p>
          : data.clients.map((g) => <ClientRow key={g.client_id} g={g} onRemind={remind} sending={sending} navigate={navigate} />)}
      </CardContent>
    </Card>
  );
}
